export type DocumentCountry = 'CL' | 'PE' | 'CO' | string;

export const DocumentValidator = {
  cleanRut: (rut: string): string => {
    return (rut || '').replace(/[^0-9kK]/g, '').toUpperCase();
  },

  formatRut: (rut: string): string => {
    const clean = DocumentValidator.cleanRut(rut);
    // Mientras el usuario escribe, no formatear hasta tener un RUT viable
    // (mínimo 7 caracteres = 6 dígitos + DV)
    if (clean.length < 7) return clean;

    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);

    let formattedBody = '';
    for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
      formattedBody = body[i] + formattedBody;
      if ((count + 1) % 3 === 0 && i !== 0) {
        formattedBody = '.' + formattedBody;
      }
    }

    return `${formattedBody}-${dv}`;
  },

  validateRut: (rut: string): boolean => {
    const clean = DocumentValidator.cleanRut(rut);
    if (clean.length < 7 || clean.length > 9) return false;

    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    if (!/^\d+$/.test(body)) return false;

    let sum = 0;
    let multiplier = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i], 10) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }

    const remainder = 11 - (sum % 11);
    let computedDv: string;
    if (remainder === 11) computedDv = '0';
    else if (remainder === 10) computedDv = 'K';
    else computedDv = String(remainder);

    return computedDv === dv;
  },

  validateDniPe: (dni: string): boolean => {
    const clean = (dni || '').replace(/\D/g, '');
    return /^\d{8}$/.test(clean);
  },

  validateCedulaCo: (cedula: string): boolean => {
    const clean = (cedula || '').replace(/\D/g, '');
    return /^\d{6,10}$/.test(clean);
  },

  validateByCountry: (
    document: string,
    countryCode: DocumentCountry
  ): boolean => {
    if (!document || document.trim().length === 0) return false;

    switch ((countryCode || '').toUpperCase()) {
      case 'CL':
        return DocumentValidator.validateRut(document);
      case 'PE':
        return DocumentValidator.validateDniPe(document);
      case 'CO':
        return DocumentValidator.validateCedulaCo(document);
      default:
        return document.trim().length > 0;
    }
  },

  formatByCountry: (document: string, countryCode: DocumentCountry): string => {
    if (!document) return '';

    switch ((countryCode || '').toUpperCase()) {
      case 'CL':
        return DocumentValidator.formatRut(document);
      default:
        return document;
    }
  },

  /**
   * Devuelve la entrada normalizada y truncada al máximo de caracteres permitido
   * por país. Pensado para usarse en el `getValueFromEvent` de un Form.Item.
   */
  normalizeInputByCountry: (
    document: string,
    countryCode: DocumentCountry
  ): string => {
    const upper = (countryCode || '').toUpperCase();

    if (upper === 'CL') {
      // Máx 9 chars limpios (8 dígitos + DV); luego se formatea con puntos y guión.
      const clean = DocumentValidator.cleanRut(document).slice(0, 9);
      return DocumentValidator.formatRut(clean);
    }
    if (upper === 'PE') {
      return (document || '').replace(/\D/g, '').slice(0, 8);
    }
    if (upper === 'CO') {
      return (document || '').replace(/\D/g, '').slice(0, 10);
    }
    return (document || '').slice(0, 20);
  },
};
